import type { DocumentAccessManager } from 'core/hooks';
import { useMyUser } from 'core/hooks';
import type { BaseEntity, PaginationResponse, UserRoles } from 'core/base';
import type { ModalProps } from 'core/components';
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components';
import { UsersList } from './UsersList';
import { useRolesByRole, useUsers, useDataSource } from './hooks';
import { UpdateDocumentAccessRoles } from './UpdateDocumentAccessRoles';
import { UpdateUserRoles } from './UpdateUserRoles';
import { getRoleOptions } from './utils';
import type { RowData } from './types';
import type { UserModel } from '../../services';
import type { UserId } from '../../types';

type UsersModalProps = {
  document?: BaseEntity;
  accessManager?: DocumentAccessManager;
  modalProps?: ModalProps;
  isAccessStatus?: boolean;
};

export function UsersModal({
  document,
  isAccessStatus = true,
  accessManager,
  modalProps,
}: UsersModalProps) {
  const user = useMyUser();
  const { data: users, isLoading } = useUsers(modalProps?.open);
  const { data: role } = useRolesByRole(modalProps?.open);
  const [allUsers, setAllUsers] = useState<PaginationResponse<UserModel>>();

  useEffect(() => {
    if (users) setAllUsers(users);
  }, [users]);

  const dataSource = useDataSource(document, user, allUsers, accessManager);

  const updateUsersRole = (listIds: UserId[], role: UserRoles[]) => {
    if (!allUsers) return;
    // @ts-expect-error Need to refactor to generic
    const newUserList = allUsers.items.reduce((previousValue: UserModel[], currentValue) => {
      if (listIds.includes(currentValue.id)) {
        return [...previousValue, { ...currentValue, roles: role }];
      }
      return [...previousValue, currentValue];
    }, []);
    // @ts-ignore
    setAllUsers({ ...allUsers, items: newUserList });
  };

  const isNoGrantedAccess = useMemo(() => {
    if (document) return !isAccessStatus;
    return user ? !user.atLeastAdmin() : isAccessStatus;
  }, [isAccessStatus, document, user]);

  const roleOptions = useMemo(() => {
    if (!user || !role) return [];

    const getUserList = () => {
      if (!document) return role.getAdminInstanceRoles(); // For the 'Instance' level

      // For 'Resource' level
      if (user.atLeastAdmin()) return role.getAdminResourceRoles();

      if (accessManager) {
        const userDocumentAccess = accessManager.getUserAccess(user.id);

        if (userDocumentAccess.atLeastOwner()) return role.getAdminResourceRoles(); // If the user is the 'Owner'
        if (userDocumentAccess.atLeastMember()) return role.getMemberResourceRoles(); // If the user is the 'Member'
      }

      return role.getGuestResourceRoles(); // If the user is the 'Guest'
    };

    return getRoleOptions(getUserList());
  }, [document, role, user, accessManager]);

  const renderRoles = (value: UserRoles, row: RowData) => {
    const isAccessSelect = document && accessManager;
    const props = {
      userId: row.id,
      updateUsersRole,
      selectProps: {
        value,
        disabled: isNoGrantedAccess,
        allowClear: isAccessSelect ? user?.id !== row.id : false, // Clear only access not user roles
        options: roleOptions,
        defaultValue: value,
      },
    };

    if (isAccessSelect) {
      return (
        <UpdateDocumentAccessRoles docId={document._id} accessManager={accessManager} {...props} />
      );
    }
    return <UpdateUserRoles {...props} />;
  };

  return (
    <Modal width={600} footer={null} {...modalProps}>
      <UsersList
        document={document}
        accessManager={accessManager}
        user={user}
        disabled={isNoGrantedAccess}
        renderRoles={renderRoles}
        roleOptions={roleOptions}
        updateUsersRole={updateUsersRole}
        dataSource={dataSource || []}
        isLoading={isLoading}
      />
    </Modal>
  );
}
